/**
 * Which piece of a split a POINT belongs to — the one rule for the split and
 * for everything that later reads its pieces back.
 *
 * The split hands a removed scanned object to a room's `demolished` bucket by
 * its stored position (`records.js`); the estimate, long after, hands the same
 * object to that room's as-found copy (`labor/splitFamilies.js`). If the two
 * answered differently for a point in neither piece — in the wall's own body,
 * or just outside the room — one room would price the removal while the other
 * listed the object. So both ask this:
 *
 *   - in or on a piece (`IN_PIECE_TOL_M`, 1 cm) → that piece, first match;
 *   - in neither → the piece whose outline is nearer;
 *   - a tie, or no point at all → `fallback` (the piece that keeps the room).
 *
 * Pure. X-Z plane.
 */

const { isPointInsideOrOnPolygon } = require('../geometry/pointInPolygon');
const { distanceToPolygonBoundary } = require('./geometry');

/** A point this close to a piece is in it (membership tolerance). */
const IN_PIECE_TOL_M = 0.01;

/**
 * @param {{x,z}|null|undefined} p
 * @param {{ key: *, ring: {x,z}[] }[]} pieces
 * @param {*} fallback the key a tie or a missing point goes to
 * @returns {*} a piece's `key`, or `fallback`
 */
function pieceHoldingPoint(p, pieces, fallback) {
  if (!p) return fallback;
  const usable = (pieces || []).filter((pc) => pc?.ring?.length >= 3);
  for (const pc of usable) {
    if (isPointInsideOrOnPolygon(p, pc.ring, IN_PIECE_TOL_M)) return pc.key;
  }
  let best = fallback;
  let bestD = Infinity;
  let tied = false;
  for (const pc of usable) {
    const d = distanceToPolygonBoundary(p, pc.ring);
    if (d < bestD) {
      best = pc.key;
      bestD = d;
      tied = false;
    } else if (d === bestD) {
      tied = true;
    }
  }
  return tied ? fallback : best;
}

module.exports = { IN_PIECE_TOL_M, pieceHoldingPoint };
